import { useSeoMeta } from '@unhead/react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle, CircleDot } from 'lucide-react';
import { SiteLayout } from '@/components/SiteLayout';
import { CATEGORIES } from '@/data/problems';
import { progressKey, useProgress } from '@/hooks/useProgress';
import NotFound from './NotFound';

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const category = CATEGORIES.find((c) => c.id === categoryId);
  const { progress } = useProgress();

  useSeoMeta({
    title: category ? `${category.name} — JSBat` : 'Not found — JSBat',
    description: category?.blurb ?? 'JavaScript practice problems.',
  });

  if (!category) return <NotFound />;

  const solved = category.problems.filter((p) => progress[progressKey(category.id, p.id)]?.solved).length;

  return (
    <SiteLayout>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> All categories
        </Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-mono text-4xl font-extrabold tracking-tight sm:text-5xl">{category.name}</h1>
            <p className="mt-3 text-lg text-muted-foreground">{category.blurb}</p>
          </div>
          <p className="font-mono text-sm text-muted-foreground">
            {solved} of {category.problems.length} solved
          </p>
        </div>

        <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {category.problems.map((p) => {
            const state = progress[progressKey(category.id, p.id)];
            return (
              <li key={p.id}>
                <Link
                  to={`/problems/${category.id}/${p.id}`}
                  className="group flex items-center gap-3 rounded-lg border bg-card px-4 py-3 shadow-xs transition-colors hover:border-brand hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {state?.solved ? (
                    <CheckCircle2 className="size-5 shrink-0 text-success" aria-label="Solved" />
                  ) : state?.attempted ? (
                    <CircleDot className="size-5 shrink-0 text-amber-500" aria-label="Attempted" />
                  ) : (
                    <Circle className="size-5 shrink-0 text-muted-foreground/50" aria-label="Not attempted" />
                  )}
                  <span className="font-mono font-medium group-hover:text-brand-strong">{p.fn}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </SiteLayout>
  );
}
